import { useCallback, useEffect, useState } from "react";
import { useAuth } from "./useAuth";
import { supabase } from "@/integrations/supabase/client";
import { trackFeature } from "@/lib/analytics";

/**
 * "Show Kin on your Apple Calendar" (#1647).
 *
 * The OTHER direction from useAppleCalendar, which reads her iCloud share link
 * into Kin. Apple has no API for writing into someone's calendar, so Kin → Apple
 * is a calendar Apple SUBSCRIBES to: one private token, one webcal:// URL, one
 * tap. From then on Apple pulls kin-calendar-feed on its own schedule.
 *
 * Every state this hook reports is read back from the database — never assumed
 * from a write (standing guardrail 16). `enable()` inserts and reads the row
 * back BEFORE it says "active"; if the insert returns no row, she gets an error
 * and no link, because a link to a token that never landed would 404 the
 * moment Apple polled it.
 *
 * The hook NEVER navigates. `enable()` resolves after a network round-trip,
 * and outside the Median shell a window.open() that far from her tap is
 * popup-blocked (the exact lesson of the .ics handoff, see appleIcsHandoff.ts).
 * KinCalendarFeedPanel decides: inside Median it hands the URL to the bridge
 * (a native call, no activation deadline); on the web it renders a real
 * <a href="webcal://…"> she taps herself.
 */

/** Path segment served by vercel.json → kin-calendar-feed. Mirrors the entry in
 *  swNavigationDenylist.ts; the test there pins both together. */
export const FEED_PATH_PREFIX = "/feed/";

/** webcal:// is what makes iOS offer "Subscribe" instead of "Download". Apple
 *  turns it into https:// and polls. */
export function feedSubscribeUrl(token: string, host: string = window.location.host): string {
  return `webcal://${host}${FEED_PATH_PREFIX}${token}`;
}

/** Two randomUUIDs with the dashes stripped: 64 hex chars, 244 bits. The
 *  Edge Function rejects anything that is not exactly this shape. */
export function mintFeedToken(): string {
  return `${crypto.randomUUID()}${crypto.randomUUID()}`.replace(/-/g, "");
}

interface FeedRow {
  id: string;
  token: string;
  created_at: string;
}

export type FeedActionResult = { ok: true; url: string } | { ok: false; error: string };

function firstRow(data: unknown): FeedRow | null {
  const r = Array.isArray(data) ? data[0] : data;
  if (!r || typeof r !== "object") return null;
  const row = r as Partial<FeedRow>;
  return typeof row.id === "string" && typeof row.token === "string" && typeof row.created_at === "string"
    ? (row as FeedRow)
    : null;
}

/** What the hook returns — exported so a host can own ONE instance and pass it
 *  to both the row summary and the panel (see KinCalendarFeedPanel). */
export interface KinCalendarFeed {
  loading: boolean;
  active: boolean;
  since: string | null;
  subscribeUrl: string | null;
  enable: () => Promise<FeedActionResult>;
  disable: () => Promise<FeedActionResult>;
  refresh: () => Promise<void>;
}

export function useKinCalendarFeed(): KinCalendarFeed {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [loading, setLoading] = useState(true);
  const [row, setRow] = useState<FeedRow | null>(null);

  const refresh = useCallback(async () => {
    if (!userId) {
      setRow(null);
      setLoading(false);
      return;
    }
    try {
      const { data, error } = await (supabase.from as any)("kin_calendar_feed_tokens")
        .select("id, token, created_at")
        .eq("user_id", userId)
        .is("revoked_at", null)
        .order("created_at", { ascending: false })
        .limit(1);
      if (error) {
        console.error("[useKinCalendarFeed] read failed", error);
        setRow(null);
      } else {
        setRow(firstRow(data));
      }
    } catch (err) {
      // A test double without every builder method, or a transport failure:
      // read as "not showing", never as "showing".
      console.error("[useKinCalendarFeed] read threw", err);
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const enable = useCallback(async (): Promise<FeedActionResult> => {
    if (!userId) return { ok: false, error: "Please sign in first." };
    const token = mintFeedToken();
    let inserted: FeedRow | null = null;
    try {
      const { data, error } = await (supabase.from as any)("kin_calendar_feed_tokens")
        .insert({ user_id: userId, token })
        .select("id, token, created_at");
      if (error) return { ok: false, error: error.message ?? "The link didn't save." };
      inserted = firstRow(data);
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : "The link didn't save." };
    }
    // Guardrail 16 — an insert that matched no row returns 200 with no rows.
    // Nothing below may run unless the row came back.
    if (!inserted) return { ok: false, error: "The link didn't save." };
    setRow(inserted);
    const url = feedSubscribeUrl(inserted.token);
    trackFeature("kin_calendar_feed_enable");
    return { ok: true, url };
  }, [userId]);

  const disable = useCallback(async (): Promise<FeedActionResult> => {
    if (!userId || !row) return { ok: false, error: "Nothing to stop." };
    try {
      const { data, error } = await (supabase.from as any)("kin_calendar_feed_tokens")
        .update({ revoked_at: new Date().toISOString() })
        .eq("id", row.id)
        .eq("user_id", userId)
        .select("id");
      if (error) return { ok: false, error: error.message ?? "Couldn't stop it." };
      const touched = Array.isArray(data) ? data.length : data ? 1 : 0;
      if (touched === 0) return { ok: false, error: "Couldn't stop it." };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : "Couldn't stop it." };
    }
    const url = feedSubscribeUrl(row.token);
    setRow(null);
    trackFeature("kin_calendar_feed_disable");
    return { ok: true, url };
  }, [userId, row]);

  return {
    loading,
    active: !!row,
    since: row?.created_at ?? null,
    subscribeUrl: row ? feedSubscribeUrl(row.token) : null,
    enable,
    disable,
    refresh,
  };
}
